interface MimicQualityTier {
  /** Max framerate mimics are rendered at */
  fps: number
  /** Height of the shared main OffscreenCanvas that mimics fan out from */
  height: number
}

const TIERS: MimicQualityTier[] = [
  { fps: 60, height: 288 },
  { fps: 30, height: 288 },
  { fps: 30, height: 196 },
  { fps: 30, height: 144 },
  { fps: 15, height: 144 },
  { fps: 10, height: 90 },
  { fps: 5, height: 72 },
  { fps: 1, height: 72 },
]

// Start in the middle: low-end machines shouldn't have to suffer through the
// high-end tiers before degrading; capable machines recover upwards instead.
const DEFAULT_TIER_INDEX = TIERS.findIndex(
  (tier) => tier.fps === 30 && tier.height === 144
)

// Fraction of main-thread time a single track's fan-out rendering is allowed
// to consume. The cost budget scales with the tier's frame interval: rendering
// less often tolerates a proportionally higher per-render cost (2ms at 30fps
// ≙ 4ms at 15fps ≙ 3% duty cycle). Without this, lowering fps would never
// relieve the cost signal and tiers would cascade pointlessly.
const RENDER_BUDGET_FRACTION = 0.03
// But a single render blocking the main thread longer than this janks the
// frame it lands in no matter how rarely it runs, so the budget is capped.
const MAX_RENDER_COST_MS = 8
// Average presented-but-missed video frames per rVFC callback. Anything
// consistently above this means the main thread can't keep up with playback.
const DROPPED_FRAMES_BUDGET = 0.5
// Degrade fast, recover slow (hysteresis) to avoid tier oscillation.
const DEGRADE_AFTER_MS = 2_000
const RECOVER_AFTER_MS = 20_000
const EMA_ALPHA = 0.1

let tierIndex = DEFAULT_TIER_INDEX
let costEma = 0
let droppedEma = 0
let pressureStart: number | null = null
let cleanStart: number | null = null

// Per-render cost budget at the current tier's framerate
const getRenderCostBudgetMs = () =>
  Math.min(
    MAX_RENDER_COST_MS,
    (RENDER_BUDGET_FRACTION * 1000) / TIERS[tierIndex].fps
  )

/**
 * Report a measurement from a mimic render loop. All tracks report into this
 * single global controller since they all share the same main thread.
 *
 * @param now rVFC timestamp (performance.now() timeline)
 * @param droppedFrames presented frames missed since the previous callback
 * @param renderCost time spent in the fan-out render, if one happened
 */
const reportMimicRender = (
  now: DOMHighResTimeStamp,
  droppedFrames: number,
  renderCost?: number
) => {
  droppedEma = droppedEma * (1 - EMA_ALPHA) + droppedFrames * EMA_ALPHA
  if (renderCost !== undefined) {
    costEma = costEma * (1 - EMA_ALPHA) + renderCost * EMA_ALPHA
  }

  const underPressure =
    costEma > getRenderCostBudgetMs() || droppedEma > DROPPED_FRAMES_BUDGET

  if (underPressure) {
    cleanStart = null
    if (pressureStart === null) {
      pressureStart = now
    } else if (
      now - pressureStart > DEGRADE_AFTER_MS &&
      tierIndex < TIERS.length - 1
    ) {
      tierIndex += 1
      pressureStart = null
      // Reset signals so the new tier is measured fresh instead of cascading
      // straight to the lowest tier on stale data.
      costEma = 0
      droppedEma = 0
    }
  } else {
    pressureStart = null
    if (cleanStart === null) {
      cleanStart = now
    } else if (now - cleanStart > RECOVER_AFTER_MS && tierIndex > 0) {
      tierIndex -= 1
      cleanStart = now
    }
  }
}

const getMimicQualityTier = (): MimicQualityTier => TIERS[tierIndex]

export {
  getMimicQualityTier,
  getRenderCostBudgetMs,
  reportMimicRender,
  type MimicQualityTier,
}
